/**
 * Voice for useChatApp: recording, transcription, spoken (AWOS) replies and
 * voice error notices. Split out of useChatApp.js unchanged
 * (2026-09-25 gap closure); state and refs come from useChatState.
 */
import { useEffect, useCallback, useRef } from 'react';
import {
  playBase64Audio,
  speakAwosText,
  transcribeVoiceBlob,
  VoiceApiError,
} from '../../api/voiceApi';
import {
  MAX_VOICE_BLOB_BYTES,
  SPOKEN_REPLY_SKIPPED_MSG,
  VOICE_AUTH_DRAFT_MSG,
  VOICE_OFFLINE_DRAFT_MSG,
} from './chatConstants';

/**
 * @param {object} s - Context from useChatState.
 * @param {object} opts - useChatApp options (scopeProfile, requiresGuardianAuth,
 *   openAuthModal, onUnauthorized).
 */
export function useChatVoice(s, {
  scopeProfile, requiresGuardianAuth, openAuthModal: openAuthModalProp, onUnauthorized: onUnauthorizedProp,
}) {
  // Callbacks read through refs so their identity never cascades into the
  // socket handler and reconnects it (2026-09-25 gap closure).
  const onUnauthorizedRef = useRef(onUnauthorizedProp);
  onUnauthorizedRef.current = onUnauthorizedProp;
  const openAuthModalRef = useRef(openAuthModalProp);
  openAuthModalRef.current = openAuthModalProp;
  const openAuthModal = useCallback(() => openAuthModalRef.current?.(), []);
  const {
    backendUrl, setInputMessage, setIsRecording, setVoiceError, chatEpochRef,
    voiceEpochRef, ttsEpochRef, voiceStartingRef, mediaRecorderRef, authTokenRef,
    audioChunksRef, audioPlayerRef, sendMessageRef,
  } = s;
  /** Stops the mic and discards the recording (sign-out, account change,
   * unmount). Uses refs and setters only, so it is safe to call anywhere. */
  const abortVoiceRecording = useCallback(() => {
    voiceEpochRef.current += 1;
    ttsEpochRef.current += 1;
    // Spoken replies belong to the account that asked (pass 10 VERIFY).
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch (err) {
        console.warn('Stopping recorder failed:', err);
      }
      recorder.stream?.getTracks().forEach((track) => track.stop());
    }
    setIsRecording(false);
  }, [audioChunksRef, audioPlayerRef, mediaRecorderRef, setIsRecording, ttsEpochRef, voiceEpochRef]);
  /** Chat switch: a recording belongs to the chat it started in, so it is
   * discarded visibly rather than transcribed and dropped (pass 11 F2). */
  const discardRecordingForChatSwitch = useCallback(() => {
    // A voice notice belongs to the chat it was raised in (pass 14).
    setVoiceError(null);
    // The previous chat's spoken reply never plays in the next one, whether
    // or not a recording is live (pass 12 F4).
    ttsEpochRef.current += 1;
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    if (!mediaRecorderRef.current && !voiceStartingRef.current) return;
    abortVoiceRecording();
    setVoiceError('Recording discarded — you switched chats.');
  }, [abortVoiceRecording, audioPlayerRef, mediaRecorderRef, setVoiceError, ttsEpochRef, voiceStartingRef]);
  // Unmount only: release the microphone.
  useEffect(() => () => abortVoiceRecording(), [abortVoiceRecording]);
  /**
   * Maps a voiceApi failure to a user-facing voiceError message, signing the
   * user out and reopening sign-in on 401 (expired/invalid token) rather than
   * showing a raw error, per the agent's VOICE_AUTH_REQUIRED contract.
   * @param {Error} err
   */
  const handleVoiceApiError = useCallback((err) => {
    if (err instanceof VoiceApiError) {
      if (err.status === 401) {
        if (onUnauthorizedRef.current) {
          onUnauthorizedRef.current('Your session expired. Please sign in again.');
        } else {
          openAuthModal();
        }
        setVoiceError('Please sign in again to use voice features.');
        return;
      }
      if (err.status === 'auth-required') {
        openAuthModal();
        return;
      }
      if (err.status === 503) {
        setVoiceError('Voice sign-in check is unavailable right now. Please try again shortly.');
        return;
      }
      if (err.status === 413) {
        setVoiceError('Recording too long — please keep voice messages shorter.');
        return;
      }
    }
    setVoiceError(err.message || 'Voice request failed');
  }, [openAuthModal, setVoiceError]);

  /**
   * Plays the spoken reply for a mic-prompted turn once the agent is done,
   * unless the account, chat or recording state moved on meanwhile.
   * @param {string} speakText
   */
  const speakReply = useCallback((speakText) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    (async () => {
      try {
        const ttsEpoch = ttsEpochRef.current;
        const voiceEpoch = voiceEpochRef.current;
        const chatEpoch = chatEpochRef.current;
        const requestToken = authTokenRef.current;
        // Skipped only because the mic is open, in the same account and
        // chat: say so rather than dropping the reply silently
        // (2026-09-25 gap closure).
        const skippedForRecording = () => {
          const micOpen = Boolean(mediaRecorderRef.current || voiceStartingRef.current);
          if (micOpen && voiceEpochRef.current === voiceEpoch
            && chatEpochRef.current === chatEpoch) {
            setVoiceError((current) => current ?? SPOKEN_REPLY_SKIPPED_MSG);
          }
          return micOpen;
        };
        let spoken;
        try {
          spoken = await speakAwosText(backendUrl, speakText, requestToken, scopeProfile);
        } catch (err) {
          // A stale failure must not sign out whoever is here now (pass 12
          // F3), nor a 401 for a token since replaced (pass 16).
          if (ttsEpochRef.current !== ttsEpoch) return;
          if (err instanceof VoiceApiError && err.status === 401
            && authTokenRef.current !== requestToken) return;
          throw err;
        }
        // Signed out, chat switched or a new recording started meanwhile,
        // or the mic is open right now: never play into it (pass 12 F2).
        if (ttsEpochRef.current !== ttsEpoch) {
          skippedForRecording();
          return;
        }
        if (skippedForRecording()) return;
        const { audio } = playBase64Audio(spoken.audioBase64, spoken.contentType);
        audioPlayerRef.current = audio;
      } catch (err) {
        console.error('AWOS TTS failed:', err);
        handleVoiceApiError(err);
      }
    })();

  }, [audioPlayerRef, ttsEpochRef, voiceEpochRef, chatEpochRef, authTokenRef, mediaRecorderRef, voiceStartingRef, setVoiceError, backendUrl, scopeProfile, handleVoiceApiError]);

  const stopVoiceRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  }, [mediaRecorderRef]);

  const startVoiceRecording = useCallback(async () => {
    if (requiresGuardianAuth) {
      openAuthModal();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Microphone not supported in this browser.');
      return;
    }
    // One recording at a time, including while the permission prompt is
    // open; a second click would orphan a live stream (pass 10 F3).
    if (voiceStartingRef.current || mediaRecorderRef.current) return;
    setVoiceError(null);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    // Start-time ownership: an abort (sign-out, account switch, unmount,
    // chat switch) during the prompt must win (pass 10 F1/F2). A new start
    // is NOT an abort: an earlier clip still transcribing keeps its words
    // (pass 11 F1).
    ttsEpochRef.current += 1;
    const voiceEpoch = voiceEpochRef.current;
    const chatEpoch = chatEpochRef.current;
    voiceStartingRef.current = true;
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } finally {
        voiceStartingRef.current = false;
      }
      if (voiceEpochRef.current !== voiceEpoch || chatEpochRef.current !== chatEpoch) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = async () => {
        setIsRecording(false);
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        if (!blob.size) {
          setVoiceError('No audio captured. Try again.');
          return;
        }
        if (blob.size > MAX_VOICE_BLOB_BYTES) {
          setVoiceError('Recording too long — please keep voice messages shorter.');
          return;
        }
        const requestToken = authTokenRef.current;
        try {
          const transcript = await transcribeVoiceBlob(backendUrl, blob, requestToken, scopeProfile);
          // The chat or account changed while transcribing: never send these
          // words somewhere else (2026-09-25 pass 9 F1).
          if (voiceEpochRef.current !== voiceEpoch) return; // account abort: silent
          if (chatEpochRef.current !== chatEpoch) {
            // Same account, other chat now: drop the words, but say so
            // (pass 12 F1) -- without hiding a newer error (pass 13).
            if (transcript) {
              setVoiceError((current) => current ?? 'Voice message discarded — you switched chats.');
            }
            return;
          }
          if (transcript) {
            // Latest sendMessage, not the one captured when recording began
            // (the socket or thread may have changed meanwhile).
            const status = sendMessageRef.current(transcript, { awosReply: true });
            if (status === 'busy') {
              // A turn started meanwhile: keep the words, never drop them
              // silently or auto-send them (pass 9 F3).
              // Append to any draft rather than overwrite it (pass 10 F4).
              setInputMessage((draft) => (draft.trim() ? `${draft} ${transcript}` : transcript));
              setVoiceError('Still answering — your words are in the box; send when ready.');
            } else if (status === 'offline' || status === 'auth-required') {
              // Not sent: keep the words in the draft, never drop them
              // (2026-09-25 gap closure).
              setInputMessage((draft) => (draft.trim() ? `${draft} ${transcript}` : transcript));
              setVoiceError(status === 'offline' ? VOICE_OFFLINE_DRAFT_MSG : VOICE_AUTH_DRAFT_MSG);
            }
          }
        } catch (err) {
          console.error('Voice transcription failed:', err);
          // A stale failure (e.g. A's 401 after B signed in) must not act on
          // the current account (pass 12 F3).
          if (voiceEpochRef.current !== voiceEpoch) return;
          // A 401 for a token the user has since replaced (same-account
          // re-sign-in) must not sign out the fresh session (pass 16).
          if (err instanceof VoiceApiError && err.status === 401
            && authTokenRef.current !== requestToken) {
            // Not a sign-out, but the words were not sent: say so (pass 17).
            setVoiceError((current) => current ?? 'Voice message not sent — please try again.');
            return;
          }
          const accountLevel = err instanceof VoiceApiError
            && (err.status === 401 || err.status === 'auth-required');
          if (chatEpochRef.current !== chatEpoch && !accountLevel) {
            // The old chat's failure is not the new chat's error (pass 15).
            setVoiceError((current) => current ?? 'Voice message discarded — you switched chats.');
            return;
          }
          handleVoiceApiError(err);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Mic permission failed:', err);
      // Aborted during the prompt: the error belongs to nobody now (pass 11 F3).
      if (voiceEpochRef.current !== voiceEpoch || chatEpochRef.current !== chatEpoch) return;
      setVoiceError(
        err?.name === 'NotAllowedError'
          ? 'Microphone permission denied.'
          : 'Could not access microphone.',
      );
    }
    // sendMessage is read through sendMessageRef (2026-09-25 gap closure).
  }, [requiresGuardianAuth, voiceStartingRef, mediaRecorderRef, setVoiceError, audioPlayerRef, ttsEpochRef, voiceEpochRef, chatEpochRef, openAuthModal, audioChunksRef, setIsRecording, authTokenRef, backendUrl, scopeProfile, sendMessageRef, setInputMessage, handleVoiceApiError]);

  return {
    abortVoiceRecording,
    discardRecordingForChatSwitch,
    handleVoiceApiError,
    speakReply,
    startVoiceRecording,
    stopVoiceRecording,
  };
}
